import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { useLanguage } from '../../../components/context/LanguageContext'
import {
  Button,
  Card,
  Table,
  Modal,
  Select,
  Space,
  message,
  Tree,
  List,
  Typography,
  Breadcrumb,
  Spin,
  Empty,
  Tooltip,
  Tag,
  Row,
  Col,
  Divider,
  Tabs,
  Upload,
  Input,
} from 'antd'
import { Content } from 'antd/es/layout/layout'
import { apiRequest } from '../../../helpers'
import {
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { AuthorizationContext } from '../../../components'
import Link from 'antd/es/typography/Link'

const { Option } = Select
const { confirm } = Modal
const { Title, Text } = Typography
const { DirectoryTree } = Tree

// Helper function to get appropriate file icon based on extension
const getFileIcon = fileName => {
  if (!fileName) return <FileUnknownOutlined />

  const extension = fileName.split('.').pop().toLowerCase()

  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return <FileImageOutlined style={{ color: '#36cfc9' }} />
    case 'pdf':
      return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
    case 'doc':
    case 'docx':
      return <FileWordOutlined style={{ color: '#1890ff' }} />
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <FileExcelOutlined style={{ color: '#52c41a' }} />
    case 'ppt':
    case 'pptx':
      return <FilePptOutlined style={{ color: '#fa8c16' }} />
    case 'zip':
    case 'rar':
    case '7z':
      return <FileZipOutlined style={{ color: '#faad14' }} />
    case 'txt':
    case 'md':
    case 'json':
    case 'xml':
    case 'html':
    case 'css':
    case 'js':
      return <FileTextOutlined style={{ color: '#722ed1' }} />
    default:
      return <FileOutlined />
  }
}

// Format file size to human-readable format
const formatFileSize = bytes => {
  if (!bytes || isNaN(bytes)) return 'Unknown'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`
}

const ChatbotStorageTab = ({ agent, refreshData }) => {
  const { t } = useLanguage()
  const { userData } = useContext(AuthorizationContext)
  const [availableStorages, setAvailableStorages] = useState([])
  const [storages, setStorages] = useState([])
  const [selectedStorage, setSelectedStorage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)

  // For file explorer
  const [currentStorage, setCurrentStorage] = useState(null)
  const [currentPath, setCurrentPath] = useState('/')
  const [breadcrumbs, setBreadcrumbs] = useState([{ path: '/', name: 'Root' }])
  const [fileList, setFileList] = useState([])
  const [fileListLoading, setFileListLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [activeTab, setActiveTab] = useState('details')
  const [fileContent, setFileContent] = useState({
    loading: false,
    textContent: '',
    htmlContent: '',
  })
  const [viewMode, setViewMode] = useState('storages') // 'storages' or 'files'

  const containerRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)

  // Add state for Add Folder modal visibility
  const [addFolderModalVisible, setAddFolderModalVisible] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')

  // Fetch available storage connectors and agent's current storage connectors
  useEffect(() => {
    const fetchStorages = async () => {
      setLoading(true)
      try {
        // Try both storage API endpoints
        let storagesResponse = await apiRequest(`${process.env.REACT_APP_API}agents/chatbot/${agent.id}/_storages`)

        if (storagesResponse && storagesResponse.data) {
          setStorages(storagesResponse.data || [])
        }
      } catch (error) {
        console.error('Error fetching storages:', error)
        message.error(t('Failed to load storage connectors'))
      } finally {
        setLoading(false)
      }
    }

    fetchStorages()
  }, [agent, t])

  // View storage connector details
  const viewStorage = storage => {
    const storageType = storage.type || (storage.baseUrl ? 'url' : 'server')
    const modal = Modal.info({
      title: t('Storage Connector Details'),
      width: 600,
      content: (
        <div>
          <p>
            <strong>{t('Name')}:</strong> {storage.name}
          </p>
          <p>
            <strong>{t('Type')}:</strong> {storageType}
          </p>
          {storage.baseUrl && (
            <p>
              <strong>{t('Base URL')}:</strong> {storage.baseUrl}
            </p>
          )}
          <p>
            <strong>{t('Can Upload')}:</strong> {storage.canUpload ? t('Yes') : t('No')}
          </p>
          <p>
            <strong>{t('Can Delete')}:</strong> {storage.canDelete ? t('Yes') : t('No')}
          </p>
          <p>
            <strong>{t('Can Process Multiple')}:</strong> {storage.canProcessMultiple ? t('Yes') : t('No')}
          </p>
          <p>
            <strong>{t('Active')}:</strong> {storage.active ? t('Yes') : t('No')}
          </p>
        </div>
      ),
      onOk() {},
    })
  }

  // Open a storage connector to browse its contents
  const openStorage = async storage => {
    setCurrentStorage(storage)
    setCurrentPath('/')
    setBreadcrumbs([{ path: '/', name: 'Root' }])
    setViewMode('files')
    await fetchFilesForPath(storage, '/')
  }

  // Fetch files for a specific path in the current storage
  const fetchFilesForPath = async (storage, path) => {
    setFileListLoading(true)
    try {
      // Use the new API endpoint to fetch storage contents
      const storageId = `${storage.type}-${storage.id}`
      const response = await apiRequest(
        `${process.env.REACT_APP_API}agents/chatbot/${agent.id}/storage/${storageId}/contents?path=${encodeURIComponent(path)}`
      )

      if (response && response.data) {
        setFileList(response.data)
      } else {
        setFileList([])
        console.error('Invalid response format:', response)
      }
    } catch (error) {
      console.error('Error fetching files:', error)
      message.error(t('Failed to load files from storage'))
      setFileList([])
    } finally {
      setFileListLoading(false)
    }
  }

  // Navigate to a folder
  const navigateToFolder = (folderPath, folderName) => {
    setCurrentPath(folderPath)

    // Update breadcrumbs
    const parts = folderPath.split('/').filter(Boolean)
    const newBreadcrumbs = [{ path: '/', name: 'Root' }]

    let currentPath = ''
    parts.forEach(part => {
      currentPath += '/' + part
      newBreadcrumbs.push({
        path: currentPath,
        name: part,
      })
    })

    setBreadcrumbs(newBreadcrumbs)
    fetchFilesForPath(currentStorage, folderPath)
  }

  // Navigate using breadcrumb
  const handleBreadcrumbClick = path => {
    // Find the index of the clicked breadcrumb
    const index = breadcrumbs.findIndex(b => b.path === path)
    if (index >= 0) {
      // Create new breadcrumbs up to the clicked one
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1)
      setBreadcrumbs(newBreadcrumbs)
      setCurrentPath(path)
      fetchFilesForPath(currentStorage, path)
    }
  }

  // Go back to storage list
  const backToStorageList = () => {
    setViewMode('storages')
    setCurrentStorage(null)
    setCurrentPath('/')
    setBreadcrumbs([{ path: '/', name: 'Root' }])
    setFileList([])
  }

  // Refresh current directory
  const refreshCurrentDirectory = () => {
    if (currentStorage) {
      fetchFilesForPath(currentStorage, currentPath)
    }
  }

  // Handle file uploads to current path
  const handleFileUpload = async ({ file, onSuccess, onError }) => {
    if (!currentStorage || !currentStorage.canUpload) {
      message.error(t('This storage does not support uploads'))
      onError()
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const storageId = `${currentStorage.type}-${currentStorage.id}`
      const response = await apiRequest(
        `${process.env.REACT_APP_API}agents/chatbot/${agent.id}/storage/${storageId}/upload?path=${encodeURIComponent(currentPath)}`,
        'POST',
        formData,
        false,
        true
      )

      if (response.ok) {
        message.success(t('File uploaded successfully'))
        onSuccess()
        // Refresh the file list
        refreshCurrentDirectory()
      } else {
        message.error(t('Failed to upload file'))
        onError()
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      message.error(t('Failed to upload file'))
      onError()
    }
  }

  // Fetch content for URL files
  const fetchFileContent = async file => {
    if (!currentStorage || !file || file.type === 'folder') return

    setFileContent({
      loading: true,
      textContent: '',
      htmlContent: '',
    })

    try {
      // Determine the URL
      const fileUrl = file.url || `${currentStorage.baseUrl}${file.path}`

      // Attempt to fetch content
      const response = await fetch(fileUrl, {
        headers: {
          Accept: 'text/html,application/xhtml+xml,application/xml',
        },
        mode: 'cors',
        credentials: 'omit',
      })

      if (response.ok) {
        const htmlContent = await response.text()

        // Basic HTML-to-text conversion for preview
        const textContent = htmlContent
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
          .replace(/<[^>]*>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()

        setFileContent({
          loading: false,
          textContent: textContent,
          htmlContent: htmlContent,
        })
      } else {
        message.error(t('Failed to fetch content'))
        setFileContent({
          loading: false,
          textContent: t('Failed to load content'),
          htmlContent: t('Failed to load content'),
        })
      }
    } catch (error) {
      console.error('Error fetching file content:', error)
      message.error(t('Failed to fetch content'))
      setFileContent({
        loading: false,
        textContent: t('Failed to load content: CORS or network error'),
        htmlContent: t('Failed to load content: CORS or network error'),
      })
    }
  }

  // View file details
  const viewFileDetails = file => {
    setSelectedFile(file)
    setActiveTab('details')

    // For URL type storage and files (not folders), pre-fetch content
    const isUrl = currentStorage && (currentStorage.type === 'url' || currentStorage.baseUrl)
    if (isUrl && file.type !== 'folder') {
      fetchFileContent(file)
    }

    // Generate correct file extension and path display
    let displayExtension = file.type === 'folder' ? t('Folder') : file.extension || (file.name ? file.name.split('.').pop().toUpperCase() : 'FILE')

    let displayPath = file.path
    if (file.type === 'folder' && !displayPath.endsWith('/')) {
      displayPath += '/'
    }

    Modal.info({
      title: file.name,
      width: 700,
      content: (
        <div>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'details',
                label: t('Details'),
                children: (
                  <div>
                    <p>
                      <strong>{t('Type')}:</strong> {displayExtension}
                    </p>
                    {file.type !== 'folder' && (
                      <p>
                        <strong>{t('Size')}:</strong> {formatFileSize(file.size)}
                      </p>
                    )}
                    <p>
                      <strong>{t('Last Modified')}:</strong> {new Date(file.lastModified).toLocaleString()}
                    </p>
                    <p>
                      <strong>{t('Path')}:</strong> {displayPath}
                    </p>
                    {file.type !== 'folder' && currentStorage && (currentStorage.type === 'url' || currentStorage.baseUrl) && (
                      <p>
                        <strong>{t('URL')}:</strong>{' '}
                        <a href={file.url || `${currentStorage.baseUrl}${file.path}`} target="_blank" rel="noopener noreferrer">
                          {file.url || `${currentStorage.baseUrl}${file.path}`}
                        </a>
                      </p>
                    )}

                    {/* Knowledge Base Information */}
                    {file.indexingStatus && (
                      <>
                        <Divider orientation="left">{t('Knowledge Base Information')}</Divider>
                        <p>
                          <strong>{t('Indexing Status')}:</strong>{' '}
                          <Tag
                            color={
                              file.indexingStatus === 'completed'
                                ? 'green'
                                : file.indexingStatus === 'failed'
                                  ? 'red'
                                  : file.indexingStatus === 'pending'
                                    ? 'orange'
                                    : file.indexingStatus === 'processing' || file.indexingStatus === 'indexing'
                                      ? 'blue'
                                      : file.indexingStatus === 'duplicate'
                                        ? 'purple'
                                        : 'default'
                            }
                          >
                            {file.indexingStatus.toUpperCase()}
                          </Tag>
                        </p>
                        {file.tokenCount && (
                          <p>
                            <strong>{t('Token Count')}:</strong> {file.tokenCount}
                          </p>
                        )}
                        {file.textSize && (
                          <p>
                            <strong>{t('Text Size')}:</strong> {formatFileSize(file.textSize)}
                          </p>
                        )}
                        {file.indexedAt && (
                          <p>
                            <strong>{t('Last Indexed')}:</strong> {new Date(file.indexedAt).toLocaleString()}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ),
              },
              ...(currentStorage && (currentStorage.type === 'url' || currentStorage.baseUrl) && file.type !== 'folder'
                ? [
                    {
                      key: 'text',
                      label: t('Text Content'),
                      children: (
                        <div>
                          {fileContent.loading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                              <Spin />
                              <div style={{ marginTop: '10px' }}>{t('Loading content...')}</div>
                            </div>
                          ) : (
                            <div
                              style={{
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                padding: '10px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '4px',
                                backgroundColor: '#fafafa',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {fileContent.textContent}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: 'html',
                      label: t('HTML Source'),
                      children: (
                        <div>
                          {fileContent.loading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                              <Spin />
                              <div style={{ marginTop: '10px' }}>{t('Loading content...')}</div>
                            </div>
                          ) : (
                            <div
                              style={{
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                padding: '10px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '4px',
                                backgroundColor: '#fafafa',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {fileContent.htmlContent}
                            </div>
                          )}
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ),
      onOk() {
        setSelectedFile(null)
        setFileContent({
          loading: false,
          textContent: '',
          htmlContent: '',
        })
      },
    })
  }

  // Storage list columns
  const storageColumns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <FolderOutlined style={{ color: '#1890ff' }} />
          <Link onClick={() => openStorage(record)}>{name}</Link>
        </Space>
      ),
    },
    {
      title: t('Type'),
      key: 'type',
      render: (text, record) => {
        const type = record.type || (record.baseUrl ? 'url' : 'server')
        return <Tag color={type === 'url' ? 'blue' : type === 'server' ? 'green' : 'purple'}>{type.toUpperCase()}</Tag>
      },
    },
    {
      title: t('Status'),
      dataIndex: 'active',
      key: 'active',
      render: active => <Tag color={active ? 'success' : 'error'}>{active ? t('Active') : t('Inactive')}</Tag>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Tooltip title={t('Browse')}>
            <Button type="primary" icon={<FolderOpenOutlined />} onClick={() => openStorage(record)} />
          </Tooltip>
          <Tooltip title={t('View Details')}>
            <Button type="default" icon={<EyeOutlined />} onClick={() => viewStorage(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const handleDragEnter = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    if (!containerRef.current.contains(e.relatedTarget)) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    e => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)
      files.forEach(file => handleFileUpload({ file, onSuccess: () => {}, onError: () => {} }))
    },
    [handleFileUpload]
  )

  // Add the confirmDeleteFile function
  const confirmDeleteFile = file => {
    confirm({
      title: t('Are you sure you want to delete this file?'),
      icon: <ExclamationCircleOutlined />,
      content: t('This action cannot be undone'),
      onOk: async () => {
        try {
          const storageId = `${currentStorage.type}-${currentStorage.id}`
          await apiRequest(
            `${process.env.REACT_APP_API}agents/chatbot/${agent.id}/storage/${storageId}/file?path=${encodeURIComponent(file.path)}`,
            'DELETE'
          )
          message.success(t('File deleted successfully'))
          refreshCurrentDirectory()
        } catch (error) {
          console.error('Error deleting file:', error)
          message.error(t('Failed to delete file'))
        }
      },
      onCancel() {},
    })
  }

  // Add function to handle Add Folder
  const handleAddFolder = async () => {
    if (!newFolderName) {
      message.error(t('Folder name cannot be empty'))
      return
    }

    try {
      const storageId = `${currentStorage.type}-${currentStorage.id}`
      await apiRequest(
        `${process.env.REACT_APP_API}agents/chatbot/${agent.id}/storage/${storageId}/folder?path=${encodeURIComponent(currentPath)}`,
        'POST',
        {
          name: encodeURIComponent(newFolderName),
        },
        true
      )
      message.success(t('Folder created successfully'))
      setAddFolderModalVisible(false)
      setNewFolderName('')
      refreshCurrentDirectory()
    } catch (error) {
      console.error('Error creating folder:', error)
      message.error(t('Failed to create folder'))
    }
  }

  const handleProcessData = () => {
    if (!currentStorage || !currentStorage.canProcessMultiple) {
      message.error(t('This storage does not support processing data'))
      return
    }
    console.log(currentStorage)
    // Logic to process data
    message.success(t(`Processing data for ${currentStorage}...`))
  }

  return (
    <Content>
      <div
        ref={containerRef}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ position: 'relative' }}
      >
        {viewMode === 'storages' ? (
          /* Storage List View */
          <Card title={t('Storage Connectors')}>
            <Table
              dataSource={storages}
              columns={storageColumns}
              rowKey="id"
              loading={loading}
              pagination={false}
              locale={{ emptyText: t('No storage connectors added') }}
            />
          </Card>
        ) : (
          /* File Explorer View */
          <Card
            title={
              <Space size="middle">
                <Button type="link" icon={<ArrowLeftOutlined />} onClick={backToStorageList} style={{ marginRight: 8 }}>
                  {t('Back to Storages')}
                </Button>
                <Text strong style={{ fontSize: 16 }}>
                  {currentStorage?.name}
                </Text>
                <Tag color={currentStorage?.type === 'url' ? 'blue' : currentStorage?.type === 'server' ? 'green' : 'purple'}>
                  {(currentStorage?.type || 'unknown').toUpperCase()}
                </Tag>
                <Button icon={<UploadOutlined />} onClick={handleProcessData}>
                  {t('Process Data')}
                </Button>
              </Space>
            }
            extra={
              <Space>
                {currentStorage && currentStorage.canUpload && (
                  <Upload customRequest={handleFileUpload} showUploadList={false}>
                    <Button icon={<UploadOutlined />}>{t('Upload')}</Button>
                  </Upload>
                )}
                {/* {currentStorage && currentStorage.type === 'server' && (
                  <Button
                    icon={<FolderOpenOutlined />}
                    onClick={() => setAddFolderModalVisible(true)}
                  >
                    {t('Add Folder')}
                  </Button>
                )} */}
                <Button icon={<ReloadOutlined />} onClick={refreshCurrentDirectory}>
                  {t('Refresh')}
                </Button>
              </Space>
            }
          >
            {/* Breadcrumb navigation */}
            {/* <Breadcrumb style={{ marginBottom: 16 }}>
              {breadcrumbs.map((crumb, index) => (
                <Breadcrumb.Item 
                  key={crumb.path}
                  onClick={() => handleBreadcrumbClick(crumb.path)}
                  style={{ cursor: 'pointer' }}
                >
                  {index === 0 ? <FolderOutlined /> : null}
                  <span>{crumb.name}</span>
                </Breadcrumb.Item>
              ))}
            </Breadcrumb> */}

            {/* File list */}
            <Spin spinning={fileListLoading}>
              {fileList.length > 0 ? (
                <Table
                  columns={[
                    {
                      title: t('Name'),
                      dataIndex: 'name',
                      key: 'name',
                      render: (text, record) => (
                        <Space>
                          {record.type === 'folder' ? <FolderOutlined style={{ fontSize: 18, color: '#1890ff' }} /> : getFileIcon(record.name)}
                          {record.type === 'folder' ? (
                            <a onClick={() => navigateToFolder(record.path, record.name)}>{text}</a>
                          ) : (
                            <a onClick={() => window.open(record.url || `${currentStorage.baseUrl}${record.path}`, '_blank')}>{text}</a>
                          )}
                        </Space>
                      ),
                    },
                    {
                      title: t('Type'),
                      key: 'type',
                      render: (text, record) =>
                        record.type === 'folder' ? (
                          <Tag color="blue">{t('Folder')}</Tag>
                        ) : (
                          <Tag color="green">{record.name.split('.').pop().toUpperCase()}</Tag>
                        ),
                    },
                    {
                      title: t('Size'),
                      key: 'size',
                      render: (text, record) => (record.type !== 'folder' ? formatFileSize(record.size) : ''),
                    },
                    {
                      title: t('Text Size'),
                      key: 'textSize',
                      render: (text, record) => (record.textSize ? formatFileSize(record.textSize) : ''),
                    },
                    {
                      title: t('Indexing Status'),
                      key: 'indexingStatus',
                      render: (text, record) => {
                        if (!record.indexingStatus) return ''

                        const statusColors = {
                          pending: 'orange',
                          processing: 'blue',
                          indexing: 'blue',
                          completed: 'green',
                          failed: 'red',
                          duplicate: 'purple',
                        }

                        return <Tag color={statusColors[record.indexingStatus] || 'default'}>{record.indexingStatus.toUpperCase()}</Tag>
                      },
                    },
                    {
                      title: t('Tokens'),
                      key: 'tokenCount',
                      render: (text, record) => record.tokenCount || '',
                    },
                    {
                      title: t('Indexed At'),
                      key: 'indexedAt',
                      render: (text, record) => (record.indexedAt ? new Date(record.indexedAt).toLocaleString() : ''),
                    },
                    {
                      title: t('Actions'),
                      key: 'actions',
                      render: (text, record) => (
                        <Space size="small">
                          <Button type="text" icon={<EyeOutlined />} onClick={() => viewFileDetails(record)} />
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => confirmDeleteFile(record)} />
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={[
                    // Folders first, then files
                    ...fileList.filter(file => file.type === 'folder'),
                    ...fileList.filter(file => file.type !== 'folder'),
                  ]}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  bordered
                />
              ) : (
                <Empty description={t('This folder is empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Spin>
          </Card>
        )}
        {isDragging && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div style={{ textAlign: 'center', color: 'white' }}>
              <UploadOutlined style={{ fontSize: '48px' }} />
              <h2>{t('Drop files here to upload')}</h2>
            </div>
          </div>
        )}
      </div>
      {/* Add Add Folder modal */}
      <Modal title={t('Add Folder')} visible={addFolderModalVisible} onOk={handleAddFolder} onCancel={() => setAddFolderModalVisible(false)}>
        <Input placeholder={t('Folder Name')} value={newFolderName} onChange={e => setNewFolderName(e.target.value)} />
      </Modal>
    </Content>
  )
}

export default ChatbotStorageTab
